import { Link } from 'react-router'
import { ArrowRight, CalendarDays, Check, Clock, Footprints, GraduationCap, Heart, MapPin, Sprout, Users } from 'lucide-react'
import { mainEvent, race, registrationPhase } from '../../shared/race.config'
import { currentTier } from '../../shared/pricing'
import { formatClock, formatLongDate, formatMoney } from '../../shared/format'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { course, feet } from '../lib/course'
import { googleCalendarUrl } from '../lib/calendar'
import { Countdown, HeroLandscape, LogoBadge, LogoIcon } from '../components/brand'
import { CourseMap } from '../components/CourseMap'
import { CapacityMeter, FundraisingMeter, RegisterButton, Schedule } from '../components/race'
import { NotifyForm } from '../components/NotifyForm'
import { ButtonLink, SectionHeading } from '../components/ui'
import { faq } from '../content/faq'
import { withOrdinals } from '../lib/ordinals'

const traitIcons = [Footprints, Sprout, GraduationCap, Heart]
const price = currentTier(mainEvent).tier.cents

export function Home() {
  const { data: stats } = useApi(() => api.stats())
  const m = race.memorial
  const open = registrationPhase() === 'open'

  return (
    <>
      {/* Hero: the race logo over a Green Horizons dusk: meadow, low sun and a lone pine */}
      <section className="relative isolate overflow-hidden bg-brand-deep text-white">
        <HeroLandscape reserve={[170, 230]} className="absolute inset-0 -z-10" />
        <div className="container-page grid items-center gap-8 pb-[170px] pt-10 sm:pb-[230px] sm:pt-14 lg:grid-cols-[minmax(0,30rem)_1fr] lg:gap-16 lg:pt-16">
          <h1 className="mx-auto w-[min(80vw,30rem)] animate-fade-up drop-shadow-[0_18px_40px_rgb(0_0_0/0.35)] lg:mx-0 lg:w-full">
            <LogoBadge />
          </h1>
          <div className="flex flex-col items-center gap-6 text-center lg:items-start lg:text-left">
            <p className="display max-w-[16ch] text-[clamp(2.75rem,6vw,4.75rem)]">
              Run, walk or cheer for <span className="text-accent">{m.name}</span>
            </p>
            <p className="max-w-[34rem] text-lg text-white/85 sm:text-xl">A 5K through Busse Woods that raises money for suicide prevention.</p>
            <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-[0.95rem] text-white/85 lg:justify-start">
              <li className="flex items-center gap-2">
                <CalendarDays className="size-4" aria-hidden /> {withOrdinals(formatLongDate(race.startsAt))}
              </li>
              <li className="flex items-center gap-2">
                <Clock className="size-4" aria-hidden /> Starts {formatClock(mainEvent.startTime)}
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="size-4" aria-hidden /> {race.location.venue}, {race.location.city}, {race.location.regionCode}
              </li>
            </ul>
            <div className="flex flex-wrap justify-center gap-3 pt-1 lg:justify-start">
              <RegisterButton size="lg" variant="bright" className="text-[1.05rem]" soon="Get notified when registration opens">
                Register for {formatMoney(price)} <ArrowRight className="size-5" aria-hidden />
              </RegisterButton>
              <ButtonLink to="/donate" size="lg" variant="onDark">
                Donate
              </ButtonLink>
            </div>
          </div>
        </div>
      </section>

      {/* Fundraising + countdown */}
      <section className="border-b border-line bg-surface">
        <div className="container-page grid gap-6 py-8 md:grid-cols-[1.4fr_auto] md:items-center md:gap-12">
          {stats ? (
            <div className="flex flex-col gap-3">
              <FundraisingMeter f={stats.fundraising} />
              <Link to="/donate" className="inline-flex items-center gap-1.5 self-start font-semibold text-brand hover:underline">
                Add a gift <ArrowRight className="size-4" aria-hidden />
              </Link>
            </div>
          ) : (
            <div className="h-24" />
          )}
          <div className="flex flex-col gap-3 border-t border-line pt-6 md:border-l md:border-t-0 md:py-2 md:pl-12">
            <p className="eyebrow">Race starts in</p>
            <Countdown />
          </div>
        </div>
      </section>

      {/* Why we run */}
      <section className="container-page grid gap-10 py-16 sm:py-20 lg:grid-cols-[1fr_1.15fr] lg:gap-16" aria-labelledby="why-h">
        <div className="flex flex-col gap-6">
          <p className="eyebrow">Why we run</p>
          <h2 id="why-h" className="display text-5xl sm:text-6xl">
            July 10<sup className="ordinal">th</sup> marks <span className="whitespace-nowrap text-brand">10 years</span> without {m.name}
          </h2>
          {m.photo ? (
            <img src={m.photo} alt={m.name} className="aspect-[4/5] w-full max-w-md rounded-2xl object-cover shadow-card" />
          ) : (
            <div className="relative grid aspect-[4/3] w-full max-w-md place-items-center overflow-hidden rounded-2xl bg-brand-soft">
              <LogoIcon size={150} />
              <p className="absolute inset-x-0 bottom-0 bg-surface/80 px-4 py-2 text-center text-sm text-ink-2">A favorite photo of {m.name} will go here</p>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-8">
          <div className="prose-race flex flex-col gap-4 text-lg text-ink-2">
            {m.story.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
          <ul className="grid gap-5 sm:grid-cols-2">
            {m.traits.map((t, i) => {
              const Icon = traitIcons[i % traitIcons.length]
              return (
                <li key={t.title} className="flex gap-3.5">
                  <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-soft text-brand" aria-hidden>
                    <Icon className="size-5" />
                  </span>
                  <div>
                    <p className="font-semibold">{t.title}</p>
                    <p className="text-[0.95rem] text-ink-2">{t.detail}</p>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      </section>

      {/* Wear purple */}
      <section className="bg-brand text-on-brand">
        <div className="container-page grid items-center gap-6 py-14 sm:py-16 md:grid-cols-[auto_1fr] md:gap-12">
          <p className="display text-[clamp(3.5rem,9vw,6.5rem)]">Wear purple.</p>
          <p className="max-w-xl text-lg text-white/90">
            It was {m.name}’s favorite color. The race shirts are purple, the course is the purple line on the map, and we’d love the start line to be a sea
            of it: shirts, socks, hair, whatever you’ve got.
          </p>
        </div>
      </section>

      {/* The race */}
      <section className="container-page py-16 sm:py-20" aria-labelledby="race-h">
        <SectionHeading
          id="race-h"
          eyebrow="Take part"
          title="One race, one morning"
          lead="A chip-timed 5K for runners, walkers and strollers, with a purple race shirt and a finisher medal for everyone."
        />
        <div className="mt-10 grid gap-5 lg:grid-cols-[1.6fr_1fr]">
          <article className="flex flex-col gap-6 rounded-2xl border border-line bg-surface p-6 sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="font-display text-4xl font-extrabold uppercase leading-none">{mainEvent.name}</h3>
                <p className="mt-2 text-ink-2">
                  {withOrdinals(formatLongDate(race.startsAt))} · starts {formatClock(mainEvent.startTime)} · ages {mainEvent.minAge}+
                </p>
              </div>
              {open && <p className="font-display text-6xl font-extrabold leading-none text-brand">{formatMoney(price)}</p>}
            </div>
            <ul className="grid gap-2 text-[0.95rem] sm:grid-cols-2">
              {mainEvent.includes.map((i) => (
                <li key={i} className="flex gap-2">
                  <Check className="mt-1 size-4 shrink-0 text-brand" aria-hidden />
                  {i}
                </li>
              ))}
            </ul>
            {open ? (
              <>
                {stats && <CapacityMeter stats={stats} />}
                <div className="flex flex-wrap items-center gap-3">
                  <RegisterButton size="lg">
                    Register <ArrowRight className="size-5" aria-hidden />
                  </RegisterButton>
                  <p className="text-sm text-ink-3">Register the whole family in one checkout.</p>
                </div>
              </>
            ) : (
              <div id="notify" className="flex scroll-mt-24 flex-col gap-3 border-t border-line pt-5">
                <p className="font-semibold">Registration opens soon. Get an email the moment it does:</p>
                <NotifyForm compact />
              </div>
            )}
          </article>
          <article className="flex flex-col gap-4 rounded-2xl bg-brand-soft p-6 sm:p-8">
            <h3 className="font-display text-3xl font-extrabold uppercase leading-none">Not running?</h3>
            <p className="text-ink-2">
              You can still be part of the morning: donate in {m.name}’s memory, or join the volunteer crew on race day.
            </p>
            <div className="mt-auto flex flex-wrap gap-3 pt-2">
              <ButtonLink to="/donate">Donate</ButtonLink>
              <ButtonLink to="/volunteer">Volunteer</ButtonLink>
            </div>
          </article>
        </div>
      </section>

      {/* Course */}
      <section className="border-y border-line bg-surface py-16 sm:py-20" aria-labelledby="course-h">
        <div className="container-page grid gap-10 lg:grid-cols-[1fr_1.5fr] lg:items-center">
          <div className="flex flex-col gap-6">
            <SectionHeading
              id="course-h"
              eyebrow="The course"
              title="Follow the purple line"
              lead="Start and finish beside Grove 5 at Busse Forest South, then out and back on the forest preserve’s paved Red Loop, over Higgins Road and up past the Elk Pasture."
            />
            <ul className="grid grid-cols-3 gap-3 text-[0.95rem]">
              {[
                ['5 km', '3.1 miles'],
                ['100%', 'paved'],
                [`${feet(course.elevation.gainM)} ft`, 'total climb'],
              ].map(([v, l]) => (
                <li key={l} className="rounded-xl bg-surface-2 p-3.5">
                  <p className="font-display text-3xl font-extrabold leading-none">{v}</p>
                  <p className="mt-1 text-ink-2">{l}</p>
                </li>
              ))}
            </ul>
            <div>
              <ButtonLink to="/course" variant="secondary">
                Course map & elevation <ArrowRight className="size-4" aria-hidden />
              </ButtonLink>
            </div>
          </div>
          <CourseMap units="km" compact minimal />
        </div>
      </section>

      {/* Race morning */}
      <section className="container-page grid gap-12 py-16 sm:py-20 lg:grid-cols-2" aria-labelledby="day-h">
        <div className="flex flex-col gap-8">
          <SectionHeading id="day-h" eyebrow="Race morning" title="How the day runs" />
          <Schedule compact />
          <div className="flex flex-wrap gap-3">
            <ButtonLink to="/info" variant="secondary">
              Full race-day info
            </ButtonLink>
            <a href={googleCalendarUrl()} target="_blank" rel="noopener noreferrer" className="inline-flex h-11 items-center gap-2 rounded-lg px-4 font-semibold text-brand hover:bg-brand-soft">
              <CalendarDays className="size-4" aria-hidden /> Add to Google Calendar
            </a>
          </div>
        </div>
        <div className="flex flex-col gap-5 self-start rounded-2xl border border-line bg-surface p-6 sm:p-8">
          <Users className="size-9 text-brand" aria-hidden />
          <h2 className="display text-4xl">Bring your people</h2>
          <p className="prose-race text-lg text-ink-2">
            Family, neighbors, coworkers, the crew from the gym. Register everyone together in one checkout and come out in purple.
          </p>
          {open && (
            <div>
              <ButtonLink to="/participants" variant="secondary">
                See who’s running
              </ButtonLink>
            </div>
          )}
        </div>
      </section>

      {/* FAQ teaser */}
      <section className="border-t border-line bg-surface py-16 sm:py-20" aria-labelledby="faq-h">
        <div className="container-page grid gap-10 lg:grid-cols-[1fr_2fr]">
          <div className="flex flex-col gap-4">
            <SectionHeading id="faq-h" eyebrow="Questions" title="Good to know" />
            <Link to="/faq" className="inline-flex items-center gap-1.5 font-semibold text-brand hover:underline">
              All {faq.length} questions <ArrowRight className="size-4" aria-hidden />
            </Link>
          </div>
          <div className="divide-y divide-line border-y border-line">
            {faq
              .filter((f) => f.featured)
              .map((f) => (
                <details key={f.q} className="group py-4">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-semibold [&::-webkit-details-marker]:hidden">
                    {f.q}
                    <span className="grid size-7 shrink-0 place-items-center rounded-full border border-line-strong text-ink-2 transition-transform group-open:rotate-45" aria-hidden>
                      +
                    </span>
                  </summary>
                  <p className="prose-race mt-2 text-ink-2">{withOrdinals(f.a)}</p>
                </details>
              ))}
          </div>
        </div>
      </section>

      {/* Sponsors: a compact invitation until there are sponsors, then their logos by tier */}
      <section className="container-page py-16 sm:py-20" aria-labelledby="sponsor-h">
        {race.sponsors.length === 0 ? (
          <div className="grid items-center gap-8 rounded-2xl border border-line bg-surface p-6 sm:p-8 md:grid-cols-[1.3fr_1fr]">
            <div className="flex flex-col gap-3">
              <p className="eyebrow">Partners</p>
              <h2 id="sponsor-h" className="display text-4xl sm:text-5xl">
                Sponsor {race.name}
              </h2>
              <p className="text-ink-2">
                Local businesses cover the race costs, so more of every dollar goes to {race.cause}. Our sponsors will be shown here.
              </p>
              <p className="text-ink-2">
                Interested? Email <span className="select-all font-semibold text-ink">{race.organizer.email}</span>
              </p>
            </div>
            <ul className="grid grid-cols-3 gap-3" aria-label="Sponsor logos coming soon">
              {[0, 1, 2].map((i) => (
                <li key={i} className="grid h-20 place-items-center rounded-xl border-2 border-dashed border-line-strong px-2 text-center text-xs text-ink-3 sm:h-24 sm:text-sm">
                  Your logo here
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <SectionHeading id="sponsor-h" eyebrow="Partners" title="Thank you to our sponsors" lead={`Local businesses cover the race costs, so more of every dollar goes to ${race.cause}.`} />
              <p className="text-ink-2">
                Sponsor inquiries: <span className="select-all font-semibold text-ink">{race.organizer.email}</span>
              </p>
            </div>
            <div className="mt-8 flex flex-col gap-6">
              {race.sponsorTiers
                .map((tier) => ({ tier, list: race.sponsors.filter((sp) => sp.tier === tier) }))
                .filter((g) => g.list.length)
                .map((g) => (
                  <div key={g.tier}>
                    <p className="eyebrow mb-3">{g.tier}</p>
                    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {g.list.map((sp) => {
                        const inner = sp.logo ? <img src={sp.logo} alt={sp.name} className="max-h-14 w-auto object-contain" /> : <span className="font-semibold">{sp.name}</span>
                        return (
                          <li key={sp.name} className="grid h-24 place-items-center rounded-xl border border-line bg-surface p-3">
                            {sp.url ? (
                              <a href={sp.url} target="_blank" rel="noopener noreferrer" className="grid place-items-center">
                                {inner}
                              </a>
                            ) : (
                              inner
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                ))}
            </div>
          </>
        )}
      </section>

      {/* Closing */}
      <section className="relative isolate overflow-hidden bg-brand-deep text-white">
        <div className="container-page flex flex-col gap-6 py-14 sm:py-16 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="display text-[clamp(3rem,8vw,5.5rem)]">See you at the start line</h2>
            <p className="mt-3 text-lg text-white/85">
              {withOrdinals(formatLongDate(race.startsAt))} · {race.location.venue}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <RegisterButton size="lg" variant="bright">
              Register
            </RegisterButton>
            <ButtonLink to="/donate" size="lg" variant="onDark">
              Donate
            </ButtonLink>
          </div>
        </div>
      </section>
    </>
  )
}
